/* The chain as a TREE keyed on who holds what (hotfix 2026-09-03).
 *
 * The Overview once drew the record-table segments in LIST ORDER as one
 * column, so sibling legs read as a chain: on Nerine, Silvretta appeared
 * to HOLD Tessin; on Aurora Global FoF, Borealis and Cascadia — both held
 * by the fund through LP interests — stacked as if one held the other.
 * The record was right (every segment carries holder_id / issuer_id);
 * only the drawing ignored it.
 *
 * Here the layout is derived ONLY from holder_id → issuer_id:
 *   - every ENTITY is drawn once;
 *   - every LEG (position → instrument) is one edge into its issuer, so
 *     an issuer reached by two instruments from one holder (Nerine holds
 *     Namenaktien AND Partizipationsscheine in Silvretta) is one node
 *     with two edges into it;
 *   - siblings of one holder stand side by side, in first-seen chain
 *     order (stable — never the seq of whichever leg happened to come
 *     first);
 *   - an entity reached from two DIFFERENT parents (a diamond /
 *     co-investment: fund → SPV-A → Target and fund → SPV-B → Target) is
 *     still ONE node: it stands under the parent that reaches it first,
 *     every other parent keeps its edge and points at that node
 *     (`drawnUnder`), and whatever the entity holds is drawn once;
 *   - depth is hops from the fund along the LONGEST path: the fund is 0,
 *     what it holds directly is 1, what THAT holds is 2 — a co-invested
 *     target sits one below its deepest parent. Cycles end the branch.
 *
 * Pure and import-free so the bare node runner can unit-test it (there is
 * no vitest in this repo — see `npm run test:unit`). The segment shape is
 * the structural subset of `RtSegment` the layout needs. */

export interface ChainSegment {
  seq: number | null
  depth: number
  group: string
  holder_id: string
  holder_name: string | null
  position_id: string
  position_ids: string[]
  instrument_id: string | null
  instrument_name: string | null
  issuer_id: string | null
  issuer_name: string | null
  state?: string
}

/** Chain legs on the record (never proposed — Ale 2026-08-31), in seq order. */
export function chainOf<S extends ChainSegment>(segments: S[]): S[] {
  return segments
    .filter((s) => s.group === 'chain' && s.state !== 'proposed')
    .sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0))
}

/** The fund's own entity: the holder nobody on the chain issues to. Falls
 *  back to the shallowest holder if the record is cyclic. Before any leg
 *  exists, callers may supply the actual recorded fund-head entity ID. */
export function fundRootId(chain: ChainSegment[], knownHeadId?: string | null): string | null {
  if (chain.length === 0) return knownHeadId ?? null
  const issued = new Set(chain.map((s) => s.issuer_id).filter((id): id is string => !!id))
  const top = chain.find((s) => !issued.has(s.holder_id))
  if (top) return top.holder_id
  return chain.reduce((a, b) => (b.depth < a.depth ? b : a)).holder_id
}

export interface TreeChild<S extends ChainSegment> {
  issuerId: string | null
  issuerName: string | null
  /** The legs from the parent into this issuer — one edge each, seq order. */
  legs: S[]
  /** The issuer as a node — present on the parent that DRAWS it (the
   *  first-seen one); null on a co-investing parent, where `drawnUnder`
   *  names the parent the node stands under, and on a cycle. */
  node: TreeNode<S> | null
  /** Set when the issuer is drawn under ANOTHER parent (a diamond:
   *  fund → SPV-A → Target and fund → SPV-B → Target — one Target box,
   *  wires from both). The legs are still this parent's edges into it. */
  drawnUnder?: { id: string; name: string | null }
}

export interface TreeNode<S extends ChainSegment> {
  entityId: string
  name: string | null
  /** Hops from the fund (the fund is 0). An entity reached by several
   *  parents sits one below the DEEPEST of them. */
  depth: number
  children: TreeChild<S>[]
}

/** Depth of every entity reachable from the root: the LONGEST holder →
 *  issuer path from the fund, so an entity reached both directly and
 *  through an SPV sits below the SPV. Back-edges (an issuer that is an
 *  ancestor on the path being walked) are left out, so cycles end. */
function entityDepths(chain: ChainSegment[], root: string): Map<string, number> {
  const issuersOf = new Map<string, string[]>()
  for (const s of chain) {
    if (!s.issuer_id) continue
    const list = issuersOf.get(s.holder_id) ?? []
    if (!list.includes(s.issuer_id)) list.push(s.issuer_id)
    issuersOf.set(s.holder_id, list)
  }
  // forward edges only: drop every edge that closes a cycle
  const forward = new Map<string, string[]>()
  const onPath = new Set<string>()
  const seen = new Set<string>()
  const mark = (id: string) => {
    seen.add(id)
    onPath.add(id)
    const out: string[] = []
    for (const next of issuersOf.get(id) ?? []) {
      if (onPath.has(next)) continue
      out.push(next)
      if (!seen.has(next)) mark(next)
    }
    forward.set(id, out)
    onPath.delete(id)
  }
  mark(root)
  const depths = new Map<string, number>([[root, 0]])
  const relax = (id: string) => {
    const d = depths.get(id) ?? 0
    for (const next of forward.get(id) ?? []) {
      if ((depths.get(next) ?? -1) >= d + 1) continue
      depths.set(next, d + 1)
      relax(next)
    }
  }
  relax(root)
  return depths
}

export function diagramTree<S extends ChainSegment>(
  chain: S[],
  knownHead?: { id: string; name: string | null }
): TreeNode<S> | null {
  if (chain.length === 0 && knownHead) {
    return { entityId: knownHead.id, name: knownHead.name, depth: 0, children: [] }
  }
  const root = fundRootId(chain)
  if (!root) return null
  const legsByHolder = new Map<string, S[]>()
  for (const s of chain)
    legsByHolder.set(s.holder_id, [...(legsByHolder.get(s.holder_id) ?? []), s])
  const rootName = chain.find((s) => s.holder_id === root)?.holder_name ?? null
  const depths = entityDepths(chain, root)
  // entity id → the node that draws it (every entity is placed ONCE, under
  // the parent that reaches it first in chain order)
  const placed = new Map<string, TreeNode<S>>()
  // entity id → the parent it is drawn under (absent for the root)
  const parentOf = new Map<string, { id: string; name: string | null }>()
  const build = (entityId: string, name: string | null): TreeNode<S> => {
    const node: TreeNode<S> = {
      entityId,
      name,
      depth: depths.get(entityId) ?? 0,
      children: [],
    }
    placed.set(entityId, node)
    const byIssuer = new Map<string, TreeChild<S>>()
    for (const s of legsByHolder.get(entityId) ?? []) {
      const key = s.issuer_id ?? `?${s.position_id}`
      let child = byIssuer.get(key)
      if (!child) {
        child = { issuerId: s.issuer_id, issuerName: s.issuer_name, legs: [], node: null }
        byIssuer.set(key, child)
        node.children.push(child)
      }
      child.legs.push(s)
    }
    for (const child of node.children) {
      if (!child.issuerId) continue
      const already = placed.get(child.issuerId)
      if (already) {
        // Drawn elsewhere (a co-investing parent, or an ancestor on a
        // cycle — the root itself has no parent): this parent keeps its
        // edges, the box stays where it is.
        child.drawnUnder = parentOf.get(child.issuerId) ?? { id: already.entityId, name: null }
        continue
      }
      parentOf.set(child.issuerId, { id: node.entityId, name: node.name })
      child.node = build(child.issuerId, child.issuerName)
    }
    return node
  }
  return build(root, rootName)
}

/* ── The chain as a real DAG (co-owned holdings, Ale ruling 15a–15c) ──────
 * When an asset is held by TWO OR MORE holders (a co-owned OpCo under two
 * SPVs, both holding the same Geschäftsantelle), the drawing must place it
 * ONCE at its level with ONE wire per holder into it — a real cross-wired
 * DAG, not a "drawn once, under X" pointer. These pure helpers give the
 * renderer (and the unit tests) the incoming edges per entity and which
 * issuers are co-owned; the pixel layout lives in `coownedLayout.ts`. */

/** Every leg into each issuer, in chain order — the incoming edges of the
 *  DAG. A co-owned asset has one entry per holder (2+ legs); a diamond via
 *  two instruments from one holder has one entry per instrument. */
export function incomingLegs<S extends ChainSegment>(chain: S[]): Map<string, S[]> {
  const out = new Map<string, S[]>()
  for (const s of chain) {
    if (!s.issuer_id) continue
    out.set(s.issuer_id, [...(out.get(s.issuer_id) ?? []), s])
  }
  return out
}

/** The issuers reached from TWO OR MORE distinct holders — the co-owned
 *  assets. These are what turns the tree into a DAG. */
export function coOwnedIssuers<S extends ChainSegment>(chain: S[]): Set<string> {
  const holders = new Map<string, Set<string>>()
  for (const s of chain) {
    if (!s.issuer_id) continue
    const set = holders.get(s.issuer_id) ?? new Set<string>()
    set.add(s.holder_id)
    holders.set(s.issuer_id, set)
  }
  const out = new Set<string>()
  for (const [id, set] of holders) if (set.size >= 2) out.add(id)
  return out
}

/** Does any asset on the chain have more than one holder? (The Overview
 *  draws a real DAG when it does, the fan-bar tree otherwise.) */
export function hasCoOwnership<S extends ChainSegment>(chain: S[]): boolean {
  return coOwnedIssuers(chain).size > 0
}

/** Every drawn entity's depth (hops from the fund) — the same longest-path
 *  depth the tree uses, exposed for the DAG layout. */
export function entityDepthsOf<S extends ChainSegment>(chain: S[]): Map<string, number> {
  const root = fundRootId(chain)
  if (!root) return new Map()
  return entityDepths(chain, root)
}

/** Every node of the tree, top down — one entry per DRAWN entity. */
export function diagramNodes<S extends ChainSegment>(tree: TreeNode<S> | null): TreeNode<S>[] {
  if (!tree) return []
  const out: TreeNode<S>[] = []
  const walk = (n: TreeNode<S>) => {
    out.push(n)
    for (const c of n.children) if (c.node) walk(c.node)
  }
  walk(tree)
  return out
}

/** Leg → its depth (hops from the fund; a leg held directly by the fund is
 *  1). Legs not reachable from the fund keep the record-table depth. */
export function chainDepths<S extends ChainSegment>(chain: S[]): Map<string, number> {
  const depths = new Map<string, number>()
  for (const s of chain) depths.set(s.position_id, s.depth)
  const walk = (n: TreeNode<S>) => {
    for (const c of n.children) {
      for (const leg of c.legs) depths.set(leg.position_id, n.depth + 1)
      if (c.node) walk(c.node)
    }
  }
  const tree = diagramTree(chain)
  if (tree) walk(tree)
  return depths
}

/** How many levels the chain runs — the deepest leg's depth. */
export function chainLevels<S extends ChainSegment>(chain: S[]): number {
  return Math.max(0, ...chainDepths(chain).values())
}

/* ── Joints, level names, instrument trades (diagram polish 2026-09-03) ──
 * Ale, after the reel of the new Overview: the horizontal connectors must
 * be LINKED to the verticals (no floating bars), with a curved joint the
 * radius of the boxes; a level is named by ALL its issuers; an instrument
 * trade (an issuance, a buy-back — a trade with no position) sits on the
 * instrument → issuer leg. The pure parts live here so the bare node
 * runner can prove them. */

/** Where a leg sits on a bar it shares with its siblings: `only` when it
 *  is alone (a straight wire, no bar), else the first / a middle / the
 *  last column — the two ends carry the curved corners, the middle ones a
 *  straight T-junction. */
export type Join = 'only' | 'first' | 'mid' | 'last'

export function joinsOf(ids: string[]): Map<string, Join> {
  const out = new Map<string, Join>()
  ids.forEach((id, i) => {
    out.set(
      id,
      ids.length === 1 ? 'only' : i === 0 ? 'first' : i === ids.length - 1 ? 'last' : 'mid'
    )
  })
  return out
}

/** The legs a holder fans out to, FLAT across its children in drawing
 *  order (two legs into Silvretta, then the leg into Tessin), and where
 *  each joins the holder's bar. */
export function fanJoins<S extends ChainSegment>(node: TreeNode<S>): Map<string, Join> {
  return joinsOf(node.children.flatMap((c) => c.legs.map((l) => l.position_id)))
}

/** Where each leg of a merged child (several instruments into one
 *  issuer) joins the merge bar above the issuer. */
export function mergeJoins<S extends ChainSegment>(child: TreeChild<S>): Map<string, Join> {
  return joinsOf(child.legs.map((l) => l.position_id))
}

/* ── The wire's two halves and their ANCHOR (wire fix 2026-09-03) ──
 * A wire is drawn in two halves; each half is a straight line (`v`), a
 * curved corner into the bar it shares with its siblings (`first` /
 * `last`) or a T through the bar (`mid` = a bar + a straight line). A
 * `first` corner and a straight half are both anchored from the LEFT
 * of the column (left: 50% − lw/2); a `last` corner is anchored from
 * the RIGHT (right: 50% − lw/2). On a fractional column centre Chrome
 * snaps a left- and a right-anchored box to different device pixels,
 * so the straight half stepped 1–2 px sideways where it met a `last`
 * corner (Ale, reel 3 Sep: "the wire to Namenaktien has a weird
 * curve"). The rule: when a wire's OTHER end is a `last` corner, its
 * straight half — and the arrowhead under it — take the corner's
 * anchor (`r`). One anchor per wire, one snap. */

/** One end of a wire: where it joins a bar, or `none` — a straight run
 *  out of the box above or into the box below. */
export type WireEnd = Join | 'none'

/** Whether a straight half must be right-anchored: only when the wire's
 *  other end is a `last` corner. */
export function rightAnchored(other: WireEnd): boolean {
  return other === 'last'
}

/** The class list of the STRAIGHT element of one half (`jt <side> v`,
 *  plus `r` when the other end is `last`); null when this half is a
 *  corner and has no straight element. */
export function straightClass(side: 'top' | 'bottom', end: WireEnd, other: WireEnd): string | null {
  if (end === 'first' || end === 'last') return null
  return `jt ${side} v${rightAnchored(other) ? ' r' : ''}`
}

/** The arrowhead's class: it sits at the bottom of the wire, under the
 *  bottom half, so it follows the TOP end's anchor. */
export function arrowClass(top: WireEnd): string {
  return `ah${rightAnchored(top) ? ' r' : ''}`
}

/** One level of the chain (hops from the fund), named by the record: the
 *  holders that hold at that level and EVERY issuer reached, in chain
 *  order, each once. */
export interface LevelNames {
  depth: number
  holders: string[]
  issuers: string[]
}

export function levelNames<S extends ChainSegment>(chain: S[]): LevelNames[] {
  const depths = chainDepths(chain)
  const byDepth = new Map<number, LevelNames>()
  for (const s of chain) {
    const d = depths.get(s.position_id) ?? s.depth
    let level = byDepth.get(d)
    if (!level) {
      level = { depth: d, holders: [], issuers: [] }
      byDepth.set(d, level)
    }
    if (s.holder_name && !level.holders.includes(s.holder_name)) level.holders.push(s.holder_name)
    if (s.issuer_name && !level.issuers.includes(s.issuer_name)) level.issuers.push(s.issuer_name)
  }
  return [...byDepth.values()].sort((a, b) => a.depth - b.depth)
}

/** Names for a sentence: all of them up to `max`, then "N more" as the
 *  last item ("A, B, C and 2 more"). */
export function shortNames(names: string[], max = 3): string[] {
  if (names.length <= max) return names
  return [...names.slice(0, max), `${names.length - max} more`]
}

/** Trades anchored on the instrument ITSELF — an issuance, a buy-back:
 *  those with no position. They belong on the instrument → issuer leg. */
export function instrumentTradesOf<
  T extends { position_id: string | null; instrument_id: string | null; state?: string },
>(trades: T[], instrumentId: string | null): T[] {
  if (!instrumentId) return []
  return trades.filter(
    (t) => !t.position_id && t.instrument_id === instrumentId && t.state !== 'proposed'
  )
}
